import { Box, Flex, Stack, Text, Tooltip } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DailyAlertIconSmall from '../../../assets/icons/alert-icon-small.svg';
import OfficeNotesIcon from '../../../assets/icons/office-notes-icon-small.svg';
import { ProviderName } from '../../../components/provider-name/ProviderName';
import {
  getCustomFieldValue,
  getLabImagingDisplay,
  getLabImagingValue,
  hasCustomField,
} from '../../../utils/customFieldUtils';
import PracticeStatusBox from '../practices/PracticeStatusBox';
//import { formatOfficeHours } from '../../../utils/formatOfficeHours';
import CustomFieldBadge from '../../../components/customfield-badge/CustomFieldBadge';
import CustomFieldLabel from '../../../components/customfield-label/CustomFieldLabel';
import ExternalAffordance from '../../../components/external-indicator/ExternalAffordance';
import PracticeStatusBadge from '../../../components/practice-status-badge/PracticeStatusBadge';
import { getPracticeById } from '../../../services/practices/getPracticeById';
import { CustomFields, Practice, Provider } from '../../../types';
import {
  isExternalPractice,
  isExternalProvider,
} from '../../../utils/externalEntity';
import { getNewPatientPriorityProps } from '../../../utils/getNewPatientPriorityProps';
import { sortPracticesByPacStatus } from '../../../utils/practiceUtils';
import ProviderPacStatus from './ProviderPacStatus';

type ProviderItemProps = {
  provider: Provider;
  officeHoursLoading?: boolean;
  duplicateProviders?: Record<string, string>;
  enrichedPracticesMap?: Record<string, Practice>;
};

const ProviderItem: React.FC<ProviderItemProps> = ({
  provider,
  officeHoursLoading,
  duplicateProviders,
  enrichedPracticesMap,
}) => {
  const navigate = useNavigate();
  const providerExternalTablespace = (
    provider as Provider & { externalTablespace?: string | null }
  ).externalTablespace;
  const providerIsExternal = isExternalProvider(provider);

  // Add state to force re-render of ProviderPacStatus
  const [pacStatusKey, setPacStatusKey] = useState(0);

  // Force re-render of ProviderPacStatus when provider data changes
  useEffect(() => {
    if (provider) {
      setPacStatusKey((prev) => prev + 1);
    }
  }, [
    provider,
    provider?.providerBridge,
    provider?.relatedPractices,
  ]);

  const newPatientProps = getNewPatientPriorityProps(
    getCustomFieldValue(provider.customFields, 'New-Patient'),
  );

  const [enrichedPractices, setEnrichedPractices] = useState<
    Practice[]
  >([]);

  useEffect(() => {
    const normalizeCustomFields = (
      practice: Practice,
    ): CustomFields | undefined => {
      const practiceWithPossibleFields = practice as Practice & {
        custom_fields?: CustomFields;
      };

      return (
        (practiceWithPossibleFields.customFields as CustomFields) ||
        practiceWithPossibleFields.custom_fields ||
        undefined
      );
    };

    const needsPracticeEnrichment = (customFields?: CustomFields) => {
      if (!customFields) return true;
      const managerLine = getCustomFieldValue(
        customFields,
        'Manager-Line',
      );
      const managerName = getCustomFieldValue(
        customFields,
        'Manager-Name',
      );
      const managerEmail = getCustomFieldValue(
        customFields,
        'Manager-Email',
      );
      return !managerLine && !managerName && !managerEmail;
    };

    const enrichPractice = async (
      practice: Practice,
    ): Promise<Practice> => {
      const usingParentBatchedMap =
        enrichedPracticesMap !== undefined;
      const preFetchedPractice = enrichedPracticesMap?.[practice.id];
      if (usingParentBatchedMap) {
        // In batched mode, never fan out into per-practice get calls.
        // If a specific practice is not present in the map yet, render the
        // existing practice payload and let the next render hydrate from map.
        if (preFetchedPractice) {
          return {
            ...practice,
            ...preFetchedPractice,
            customFields:
              normalizeCustomFields(preFetchedPractice) ||
              normalizeCustomFields(practice) ||
              ({} as CustomFields),
          };
        }

        return {
          ...practice,
          customFields:
            normalizeCustomFields(practice) || ({} as CustomFields),
        };
      }

      const existingCustomFields = normalizeCustomFields(practice);

      if (!needsPracticeEnrichment(existingCustomFields)) {
        return {
          ...practice,
          customFields:
            existingCustomFields ||
            (practice.customFields as CustomFields) ||
            {},
        };
      }

      try {
        const { data } = await getPracticeById(practice.id);
        if (data?.practice) {
          return {
            ...practice,
            ...data.practice,
            customFields:
              data.practice.customFields ||
              existingCustomFields ||
              (practice.customFields as CustomFields) ||
              {},
          };
        }
      } catch (err) {
        console.warn(
          'ProviderItem: failed to enrich practice custom fields',
          practice.id,
          err,
        );
      }

      return {
        ...practice,
        customFields:
          existingCustomFields ||
          (practice.customFields as CustomFields) ||
          {},
      };
    };

    const hydratePractices = async () => {
      const relatedPractices = provider.relatedPractices || [];
      if (relatedPractices.length === 0) {
        setEnrichedPractices([]);
        return;
      }

      const hydrated = await Promise.all(
        relatedPractices.map((practice) => enrichPractice(practice)),
      );

      // On-PAC practices first so agents don't see "Not on PAC" at the top
      setEnrichedPractices(sortPracticesByPacStatus(hydrated));
    };

    hydratePractices();
  }, [provider, enrichedPracticesMap]);

  const getMetadataCount = (
    customField: CustomFields | undefined,
    fieldName: keyof CustomFields,
  ): number => {
    const fieldValues = customField?.[fieldName];
    return Array.isArray(fieldValues) ? fieldValues.length : 0;
  };

  const renderPracticeDetails = (practice: Practice) => {
    const externalTablespace = (
      practice as Practice & { externalTablespace?: string | null }
    ).externalTablespace;
    const practiceDisplayName =
      externalTablespace
        ? `${practice.name} (Tablespace: ${externalTablespace})`
        : practice.name;
    const practiceIsExternal = isExternalPractice(practice);

    const practiceWithPossibleFields = practice as Practice & {
      custom_fields?: CustomFields;
    };
    const customFields =
      (practiceWithPossibleFields.customFields as CustomFields) ||
      practiceWithPossibleFields.custom_fields ||
      undefined;
    const normalizedCustomFields =
      customFields ?? ({} as CustomFields);

    const dailyAlertsCount = getMetadataCount(
      customFields,
      'Daily-Alerts',
    );
    const officeNotesCount = getMetadataCount(
      customFields,
      'Office-Notes',
    );

    return (
      <Stack
        padding="0.75rem 1rem"
        gap="0.75rem"
        borderRadius="0.5rem"
        border="1px solid"
        borderColor="border.subtle"
        key={practice.id}
        minHeight="12rem"
        width="28.5rem"
        maxWidth="100%"
        overflow="hidden"
      >
        <Flex
          gap="0.5rem"
          alignItems="flex-start"
          flexWrap="wrap"
          justifyContent="space-between"
          minHeight="3rem"
        >
          <Flex
            align="center"
            gap="0.5rem"
            flex="1 1 16rem"
            wrap="wrap"
          >
            <Text
              fontSize="1.25rem"
              fontWeight="700"
              lineHeight="120%"
              color="#4A5568"
              onClick={() => navigate(`/practices/${practice.id}`)}
              _hover={{
                color: '#3175CE',
                cursor: 'pointer',
              }}
              minWidth="12rem"
              whiteSpace="normal"
              wordBreak="break-word"
              maxHeight="3rem"
              overflow="hidden"
              display="-webkit-box"
              style={{
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {practiceDisplayName}
            </Text>
            {practiceIsExternal && (
              <ExternalAffordance entityType="practice" size="sm" />
            )}
          </Flex>
          {/* Practice Status Badge - Keep badges together */}
          {practice && !practiceIsExternal && (
            <Flex gap="0.5rem" alignItems="center" flexShrink={0}>
              <PracticeStatusBadge
                active={practice.active ?? true}
                practiceOnPac={practice.practiceOnPac}
                practiceHospitalFollowupPac={practice.practiceHospitalFollowupPac}
                practiceSickCrossCoveragePac={practice.practiceSickCrossCoveragePac}
              />
            </Flex>
          )}
        </Flex>

        <PracticeStatusBox
          timezone={practice.timezone || ''}
          state={practice.state || ''}
          width="100%"
          isLoading={officeHoursLoading}
          id={practice.id}
          unFormattedOfficeHours={practice.office_hours}
        />

        <Flex gap="0.5rem" wrap="wrap" alignItems="center">
          {(
            [
              ['Labs Hours', 'Labs-Drawn-Hours'],
              ['Imaging Hours', 'Imaging-Available-Hours'],
            ] as const
          ).map(([label, fieldName]) => {
            const { displayText, bgColor } = getLabImagingDisplay(
              getLabImagingValue(normalizedCustomFields, fieldName),
            );
            return (
              <CustomFieldLabel
                key={fieldName}
                customField={label}
                fieldValue={displayText}
                fieldValueBgColor={bgColor}
              />
            );
          })}
        </Flex>

        <Flex
          gap={'1.5rem'}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Flex justifyContent={'space-between'}>
            {getCustomFieldValue(
              normalizedCustomFields,
              'Backline',
            ) && (
              <CustomFieldBadge
                customField="Backline"
                fieldValue={getCustomFieldValue(
                  normalizedCustomFields,
                  'Backline',
                )}
                bgColor="#FED7D7"
                valueColor="#9B2C2C"
                titleColor="#9B2C2C"
              />
            )}
            {getCustomFieldValue(normalizedCustomFields, 'Phone') && (
              <CustomFieldBadge
                customField="Phone"
                fieldValue={getCustomFieldValue(
                  normalizedCustomFields,
                  'Phone',
                )}
                bgColor="background.gray50"
              />
            )}
          </Flex>
          <Flex gap={'1.5rem'}>
            {hasCustomField(
              normalizedCustomFields,
              'Daily-Alerts',
            ) && (
              <Tooltip label="Daily Alerts">
                <Flex alignItems={'center'} gap={'0.25rem'}>
                  <img
                    src={DailyAlertIconSmall}
                    alt="Daily Alerts Icon Small"
                  />
                  <Text
                    color="text.primary"
                    fontSize="0.875rem"
                    fontWeight="700"
                    lineHeight="150%"
                  >
                    {dailyAlertsCount}
                  </Text>
                </Flex>
              </Tooltip>
            )}
            {hasCustomField(
              normalizedCustomFields,
              'Office-Notes',
            ) && (
              <Tooltip label={'Office Notes'}>
                <Flex alignItems={'center'} gap={'0.25rem'}>
                  <img src={OfficeNotesIcon} alt="Office Notes" />
                  <Text
                    color="text.primary"
                    fontSize="0.875rem"
                    fontWeight="700"
                    lineHeight="150%"
                  >
                    {officeNotesCount}
                  </Text>
                </Flex>
              </Tooltip>
            )}
          </Flex>
        </Flex>
      </Stack>
    );
  };

  return (
    <Flex
      borderRadius="0.5rem"
      borderColor="border.subtle"
      borderWidth="1px"
      p={'0.75rem 1rem'}
      alignItems="flex-start"
      justifyContent="space-between"
    >
      <Box>
        <Flex alignItems="center" gap="0.5rem" flexWrap="wrap">
          <ProviderName
            provider={provider}
            duplicateProviders={duplicateProviders}
            mode="link"
            size="lg"
            truncate={false}
          />
          {providerExternalTablespace && (
            <CustomFieldLabel
              customField="Tablespace"
              fieldValue={providerExternalTablespace}
              fieldValueBgColor="background.gray100"
            />
          )}
          {!providerIsExternal && (
            <ProviderPacStatus
              key={pacStatusKey} // Force re-render when provider data changes
              providerBridge={provider?.providerBridge}
              relatedPractices={provider?.relatedPractices}
            />
          )}
          <CustomFieldLabel
            customField="Athena ID"
            fieldValue={
              getCustomFieldValue(
                provider?.customFields,
                'Athena-ID',
              ) || 'Not specified'
            }
            fieldValueBgColor={
              getCustomFieldValue(
                provider?.customFields,
                'Gender',
              ) === 'Male'
                ? 'blue.100'
                : getCustomFieldValue(
                      provider?.customFields,
                      'Gender',
                    ) === 'Female'
                  ? 'background.pink100'
                  : 'gray.100'
            }
            fieldValueTextColor={
              getCustomFieldValue(
                provider?.customFields,
                'Gender',
              ) === 'Male'
                ? 'blue.800'
                : getCustomFieldValue(
                      provider?.customFields,
                      'Gender',
                    ) === 'Female'
                  ? 'text.pink800'
                  : 'gray.800'
            }
          />
        </Flex>
        <Flex mt="0.375rem" gap="0.5rem" wrap="wrap">
          {provider?.pronunciation && (
            <CustomFieldLabel
              customField="Pronunciation"
              fieldValue={`${provider?.pronunciation}`}
              fieldValueBgColor="background.teal100"
              fieldValueTextColor="text.primary"
            />
          )}
          {provider?.nickname && (
            <CustomFieldLabel
              customField="Nickname"
              fieldValue={`${provider?.nickname}`}
              fieldValueBgColor="background.teal100"
              fieldValueTextColor="text.primary"
            />
          )}
          {newPatientProps?.fieldValue && (
            <CustomFieldLabel
              customField="New Patient"
              fieldValue={newPatientProps.fieldValue}
              fieldValueBgColor={newPatientProps.fieldValueBgColor}
              fieldValueTextColor={
                newPatientProps.fieldValueTextColor
              }
              hideCustomFieldLabel={true}
            />
          )}
          {getCustomFieldValue(provider.customFields, 'Age') && (
            <CustomFieldLabel
              customField="Treats"
              fieldValue={getCustomFieldValue(
                provider.customFields,
                'Age',
              )}
              fieldValueBgColor="background.teal100"
              fieldValueTextColor="text.primary"
            />
          )}
          {provider.specialty && (
            <CustomFieldLabel
              customField="Speciality"
              fieldValue={provider.specialty}
              fieldValueBgColor="background.blue100"
              fieldValueTextColor="text.primary"
            />
          )}
        </Flex>
      </Box>
      <Box>
        {enrichedPractices.length ? (
          <Stack spacing="0.25rem">
            {enrichedPractices.map((practice) =>
              renderPracticeDetails(practice),
            )}
          </Stack>
        ) : (
          <Flex width={'28.5rem'}>
            <Text fontSize="0.875rem" color="gray.500">
              No related practices available
            </Text>
          </Flex>
        )}
      </Box>
    </Flex>
  );
};

export default ProviderItem;
